import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import BaseTeamPage from './BaseTeamPage';
import StateObserver, { now, user } from 'src/StateObserver';
import {
  animDuration,
  createWaiterInView,
  whileInHierarchy,
  withLoading,
} from 'src/lib/utils';
import { Avatar } from 'src/game/components/shared/Avatar';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import {
  FlexBoxImageScaleView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import { gsap } from 'gsap';
import { ChatEntry } from './ChatEntry';
import { GCInstant } from 'src/lib/gcinstant';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import { ExtendedChatMessage } from 'src/replicant/team/team.asyncGetters';
import Scroll from 'src/game/components/shared/Scroll';
import { duration } from 'src/replicant/utils/duration';
import {
  getCurrentTeamProfile,
  getTeamChatMessages,
  getTeamMemberCount,
  trackTeamInteraction,
} from '../team.sequence';
import {
  DISPLAYED_CHAT_MESSAGES,
  energyRequestCooldown,
} from 'src/replicant/team/team.ruleset';
import { formatMatch2Duration } from '../../match2.util';
import i18n from 'src/lib/i18n/i18n';
import * as Inventory from 'src/replicant/inventory';
import { TeamChatMessage } from 'src/replicant/team/team.actions';
import { createEmitter } from 'src/lib/Emitter';

export default class TeamChatPage extends BaseTeamPage {
  private footer!: FlexBoxImageScaleView;
  private scroll!: Scroll<ExtendedChatMessage, ChatEntry>;
  private requestEnergyButton!: ButtonScaleViewWithText;
  private message!: LangBitmapFontTextView;
  private waiter = createWaiterInView(this);
  private pendingMessages: ExtendedChatMessage[] = [];
  private lastSeenAt = 0;

  constructor(private superview: View) {
    super(superview);
    this.addFooter();
    this.addContent();
    this.addHeader();
    this.lastSeenAt = user().team.lastSeenMessageTimestamp;
    void whileInHierarchy(this, () => this.fetchMessages());

    // Trigger fetching of team member count for caching.
    getTeamMemberCount(true);

    createEmitter(this, ({ ui }) => ui.screenSize).addListener((screen) => {
      this.updateOpts({
        height: screen.height - superview.style.y,
      });
      this.footer.updateOpts({
        y: this.style.height - 130,
      });
      this.scroll.resizeScroll({
        height:
          this.style.height -
          this.header.style.height -
          this.footer.style.height,
      });
    });
  }

  private async addHeader() {
    const profile = await withLoading(
      async () => getCurrentTeamProfile()?.get(),
      {
        superview: this.header,
        color: '#F24463',
        feature: Feature.TEAM._,
        subFeature: Feature.TEAM.GET_TEAM_PROFILE,
      },
    );

    new Avatar({
      superview: this.header,
      x: 55,
      y: this.header.style.height * 0.5,
      width: 70,
      height: 70,
      image: profile?.icon,
    });

    new LangBitmapFontTextView({
      superview: this.header,
      font: 'Body',
      size: 34,
      color: '#F24463',
      localeText: () => profile?.name ?? '',
      width: 200,
      height: 40,
      x: 195,
      y: this.header.style.height * 0.5 - 4,
      align: 'left',
      verticalAlign: 'center',
      centerOnOrigin: true,
    });

    // Disable help requests for now and do them as a fast followup later
    // this.addAutoHelpRequestToggle();
  }

  private addFooter() {
    this.footer = new FlexBoxImageScaleView({
      superview: this,
      image: 'assets/ui/match2/team/chat/panel_chatBar.png',
      width: this.style.width,
      height: 132,
      y: this.style.height - 130,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 2, right: 2 },
        vertical: { top: 2, bottom: 2 },
      },
      flex: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    this.addEnergyRequestButton();

    getFlexNode(this.requestEnergyButton).updateStyle({
      marginLeft: 20,
    });

    const emote = new ButtonScaleView({
      superview: this.footer,
      image: 'assets/ui/match2/team/chat/btn_emote.png',
      width: 102,
      height: 107,
      onClick: async () => {
        sounds.sfx.play('ok');
        const emoteId = await openPopupPromise('popupTeamChatEmotes', {});

        if (!emoteId) {
          return;
        }

        const sentAt = await StateObserver.invoke.sendMessageToTeam({
          type: 'emote',
          id: emoteId,
        });

        this.addLocalChatMessage({
          senderId: GCInstant.playerID,
          // Sender name will be replaced by "you" anyway
          senderName: '',
          sentAt,
          seen: true,
          contents: {
            type: 'emote',
            id: emoteId as any,
          },
        });

        // Track the interaction.
        trackTeamInteraction('emoji');
      },
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.TAB_CHAT_EMOTE_BUTTON,
      },
    });

    getFlexNode(emote).updateStyle({
      marginLeft: 'auto',
    });

    const chat = new ButtonScaleView({
      superview: this.footer,
      image: 'assets/ui/match2/team/chat/btn_chat.png',
      width: 218,
      height: 104,
      onClick: async () => {
        sounds.sfx.play('ok');
        const textId = await openPopupPromise('popupTeamChatMessages', {});

        if (!textId) {
          return;
        }

        const sentAt = await StateObserver.invoke.sendMessageToTeam({
          type: 'text',
          id: textId,
        });

        this.addLocalChatMessage({
          senderId: GCInstant.playerID,
          // Sender name will be replaced by "you" anyway
          senderName: '',
          sentAt,
          seen: true,
          contents: {
            type: 'text',
            id: textId,
          },
        });

        // Track the interaction.
        trackTeamInteraction('chat');
      },
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.TAB_CHAT_CHAT_BUTTON,
      },
    });

    getFlexNode(chat).updateStyle({
      marginHorizontal: 20,
    });
  }

  private addEnergyRequestButton() {
    this.requestEnergyButton = new ButtonScaleViewWithText({
      superview: this.footer,
      image: 'assets/ui/match2/team/chat/btn_ask.png',
      imageDisabled: 'assets/ui/match2/team/chat/btn_ask_disabled.png',
      width: 248,
      height: 104,
      font: 'Body',
      fontSize: 35,
      labelOffsetX: 20,
      disabledFontOffsetY: -14,
      localeText: () => i18n('match2.team.chat.energyRequest'),
      onClick: async () => {
        sounds.sfx.play('ok');

        // Message to be sent, requests only the remaining energy.
        const message: TeamChatMessage = {
          type: 'energyRequest',
          value: Inventory.getters.getEnergy(user(), now()),
        };

        // Send message and add it to cache immediately.
        const sentAt = await StateObserver.invoke.sendMessageToTeam(message);
        this.addLocalChatMessage({
          senderId: GCInstant.playerID,
          // Sender name will be replaced by "you" anyway
          senderName: '',
          sentAt,
          seen: true,
          contents: message,
        });

        // Disable button and show cooldown.
        this.updateEnergyTimer();

        // Track the interaction.
        trackTeamInteraction('askEnergy');
      },
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.TAB_CHAT_REQUEST_ENERGY,
      },
    });

    this.message = new LangBitmapFontTextView({
      superview: this.requestEnergyButton,
      centerOnOrigin: true,
      x: this.requestEnergyButton.style.width / 2 + 20,
      y: this.requestEnergyButton.style.height / 2 + 18,
      font: 'Title',
      color: '#FFFCBE',
      size: 22,
      width: 120,
      height: 40,
      visible: false,
      align: 'center',
      verticalAlign: 'center',
    });

    this.updateEnergyTimer();
  }

  private updateEnergyTimer() {
    // Request on cooldown.
    const timeSinceLastRequest =
      StateObserver.now() - user().team.lastEnergyRequestTimestamp;
    if (timeSinceLastRequest < energyRequestCooldown) {
      this.requestEnergyButton.setDisabled(true);

      this.message.show();
      this.message.localeText = () =>
        formatMatch2Duration(energyRequestCooldown - timeSinceLastRequest);

      this.waiter.wait(
        this.updateEnergyTimer.bind(this),
        duration({ seconds: 1 }),
      );

      return;
    }

    // Can only send request if we have less than max energy.
    const userState = user();
    if (
      Inventory.getters.getEnergy(userState, now()) >=
      Inventory.getters.getMaxEnergy(userState)
    ) {
      this.requestEnergyButton.setDisabled(true);
      this.message.localeText = () => i18n('match2.team.chat.max');
      this.message.show();
      return;
    }

    // Can send request.
    this.requestEnergyButton.setDisabled(false);
    this.message.hide();
  }

  private addAutoHelpRequestToggle() {
    const width = 100;
    const height = 50;
    const buttonPadding = 20;
    const bg = new ButtonScaleView({
      superview: this.header,
      image: 'assets/ui/match2/team/chat/toggle_help_off.png',
      width,
      height,
      x: this.header.style.width - 80,
      y: this.header.style.height * 0.5,
      centerOnOrigin: true,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.TAB_CHAT_AUTO_HELP_REQUEST_TOGGLE,
      },
    });

    const bgOn = new ButtonScaleView({
      superview: bg,
      image: 'assets/ui/match2/team/chat/toggle_help_on.png',
      width,
      height,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.TAB_CHAT_AUTO_HELP_REQUEST_TOGGLE,
      },
    });

    const button = new ImageScaleView({
      superview: bg,
      image: 'assets/ui/match2/team/chat/toggle_help_btn.png',
      width: 74,
      height: 68,
      y: height / 2,
      x: width - buttonPadding,
      centerOnOrigin: true,
    });

    let on = StateObserver.getState().user.team.autoCareRequest;

    bg.onClick = async () => {
      on = !on;

      gsap.to(button.style, {
        duration: 0.3,
        x: on ? width - buttonPadding : buttonPadding,
        ease: 'sine.inOut',
      });

      await gsap.to(bgOn.style, {
        duration: 0.3,
        opacity: on ? 1 : 0,
        ease: 'sine.inOut',
      });

      await StateObserver.invoke.setTeamAutocareRequest(on);
    };
  }

  private addContent() {
    this.scroll = new Scroll({
      superview: this,
      rect: {
        x: 0,
        y: this.header.style.height,
        width: this.superview.style.width,
        height:
          this.superview.style.height -
          this.header.style.height -
          this.footer.style.height,
      },
      createItem: (superview, _, data) => {
        const entry = new ChatEntry({ superview });

        switch (data.contents.type) {
          case 'helpRequest':
            // Close everything to start a visit session
            entry.on('Close', () => {
              this.emit('Close');
            });
            break;
          case 'energyRequest':
            entry.on('EnergySent', () => {
              // Update the energy request locally until the server responds
              this.addLocalChatMessage({
                ...data,
                contents: {
                  ...data.contents,
                  value: data.contents.value! + 1,
                },
                canRespond: false,
              });
            });
            break;
        }

        entry.style.x = (superview.style.width - entry.style.width) * 0.5;
        entry.setup(data);
        return entry;
      },
      recycle: false,
    });
  }

  private updateChat(scrollToBottom: boolean = false, instant = false) {
    const teamChat = getTeamChatMessages({
      lastSeenAt: this.lastSeenAt,
    })?.getCached();
    if (!teamChat) return;

    // Remove pending messages that have already been reflected in the cache.
    this.pendingMessages = this.pendingMessages.filter(
      (x) =>
        !teamChat.messages.some((y) => {
          const sameSender = x.senderId === y.senderId;
          const sameTimestamp = x.sentAt === y.sentAt;

          // Keep energy requests if they are newer.
          if (x.contents.type === 'energyRequest') {
            const newerValue = y.contents.value! >= x.contents.value!;
            return sameSender && sameTimestamp && newerValue;
          }

          return sameSender && sameTimestamp;
        }),
    );

    // Create a list of messages to display.
    const allMessages = [...teamChat.messages].filter((x) => {
      // Special case for energy requests.
      if (x.contents.type === 'energyRequest') {
        // Should remove energy requests if there is a newer help request.
        const pendingEnergyRequest = this.pendingMessages.find(
          (y) =>
            y.contents.type === 'energyRequest' && y.senderId === x.senderId,
        );
        if (pendingEnergyRequest) {
          return x.sentAt >= pendingEnergyRequest.sentAt;
        }
      }
      return true;
    });

    // Add pending messages.
    this.pendingMessages.forEach((x) => {
      // Update existing messages.
      const existing = allMessages.find(
        (y) => y.sentAt === x.sentAt && y.senderId === x.senderId,
      );
      if (existing) {
        existing.contents = x.contents;
        existing.canRespond = x.canRespond;
        return;
      }

      // Add pending messages to the end of the list.
      allMessages.push(x);
    });

    // Sort messages by timestamp.
    allMessages.sort((a, b) => a.sentAt - b.sentAt);

    // Only show the last N messages.
    const messages = allMessages.slice(-DISPLAYED_CHAT_MESSAGES);

    // Update scroll.
    this.scroll.setItems(messages, false);
    if (scrollToBottom || this.scroll.isAtBottom()) {
      this.scroll.scrollTo(0, Infinity, instant ? 0 : animDuration);
    }
  }

  private addLocalChatMessage(message: ExtendedChatMessage) {
    this.pendingMessages.push(message);
    this.updateChat(true);
  }

  public show() {
    super.show();
    void StateObserver.invoke.updateLastSeenTeamMessageTimestamp();
    this.lastSeenAt = user().team.lastSeenMessageTimestamp;
    this.updateChat(true, true);
  }

  public hide() {
    super.hide();
    void StateObserver.invoke.updateLastSeenTeamMessageTimestamp();
  }

  private async fetchMessages() {
    const cacheAPI = getTeamChatMessages({
      lookingAtChat: true,
      lastSeenAt: this.lastSeenAt,
    });
    try {
      let fetchingPromise = cacheAPI?.get();

      // Already have messages, don't show loading spinner.
      if (cacheAPI?.getCached()) {
        await fetchingPromise;
      }
      // No messages, show loading spinner.
      else {
        await withLoading(async () => fetchingPromise, {
          superview: this,
          feature: Feature.TEAM._,
          subFeature: Feature.TEAM.GET_CHAT_MESSAGES,
        });
      }

      // Skip if view is hidden.
      if (!this.style.visible) return;

      // Mark all messages as seen.
      cacheAPI?.setCached((x) => {
        if (!x) return x;
        x.data.messages.forEach((y) => (y.seen = true));
        x.data.unreadRequestCount = 0;
        x.data.unreadMessagesCount = 0;
        return x;
      });

      // Update list.
      this.updateChat();

      // Make sure last seen timestamp is updated.
      this.lastSeenAt = StateObserver.now();
    } finally {
      // Wait for cache to get stale.
      await cacheAPI?.waitForRefresh();
    }
  }
}
